import { z } from 'zod';

import { getDateRangeSearchParamsSchema } from '#utils/dates.ts';
import { BASE_APPOINTMENT_TYPES } from './Filters/AppointmentTypes.utils.ts';

const idsArraySchema = z
  .array(z.string())
  .transform((value) => value.map(Number).filter((value) => value > 0));

const formPrioritySchema = z.union([
  z.literal('all'),
  z.literal('required'),
  z.literal('optional'),
]);

const searchParamsSchema = z
  .object({
    tab: z
      .union([z.literal('scheduling'), z.literal('forms')])
      .catch('scheduling'),
    providerIds: idsArraySchema,
    appointmentTypeIds: idsArraySchema,
    addressIds: idsArraySchema,
    bookingChannelIds: z
      .array(
        z.union([z.literal('staff'), z.literal('pss'), z.literal('import')])
      )
      .catch([]),
    baseAppointmentTypes: z.array(z.enum(BASE_APPOINTMENT_TYPES)).catch([]),
    assessmentTypeIds: idsArraySchema,
    formsPriority: formPrioritySchema.catch('all'),
  })
  .transform((value) => ({
    ...value,
    tabIndex: value.tab === 'scheduling' ? (0 as const) : (1 as const),
  }));

/**
 * Parses the search params from the URL. Since the home route renders the
 * appointments and forms drilldowns as nested routes, we need to carry the
 * "base" search params into those routes in order for the home page to
 * maintain its state while the dialogs are displayed.
 */
export function getParsedSearchParams(searchParams: URLSearchParams) {
  const result = searchParamsSchema.parse({
    tab: searchParams.get('tab'),
    providerIds: searchParams.getAll('pId'),
    appointmentTypeIds: searchParams.getAll('atId'),
    addressIds: searchParams.getAll('aId'),
    bookingChannelIds: searchParams.getAll('bcId'),
    baseAppointmentTypes: searchParams.getAll('bat'),
    assessmentTypeIds: searchParams.getAll('astId'),
    formsPriority: searchParams.get('fp'),
  });

  const dateRangeResult = getDateRangeSearchParamsSchema().parse({
    from: searchParams.get('from'),
    to: searchParams.get('to'),
  });

  return { ...result, ...dateRangeResult };
}
